import React, { useState } from "react";
import * as WorkingPartServices from "../../../../services/WorkingPartServices";
import PaginateList from "../../../utils/PaginateList";
import moment from "moment-timezone";
import { useTranslation } from "react-i18next";
import {
  ButtonAction,
  Icon,
  ContainerTooltip,
  ErrorIcon,
} from "./workingPartList.styled";
import { useNavigate } from "react-router-dom";
import PATHS from "../../../../constants/paths";
import showDialog from "../../../utils/ShowDialog/ShowDialog";
import { useWorkingPart } from "../../../user/workingPartSnap/hook/useWorkingPart";

const WorkingPartListContainer = ({ filters }) => {
  const { t } = useTranslation();
  const [forceRefresh, setForceRefresh] = useState(false);
  const [selectedRows, setSelectedRows] = useState({});
  const [selectedNames, setSelectedNames] = useState([]);
  const [selectAllPages, setSelectAllPages] = useState(false);
  const [allData, setAllData] = useState([]);
  const navigate = useNavigate();
  const { putConcepts } = useWorkingPart();
  // Función para manejar la selección individual
  const handleSelectRow = (object) => {
    setSelectedRows((prev) => ({
      ...prev,
      [object.uuid]: !prev[object.uuid],
    }));
    setSelectedNames((prev) => {
      const isSelected = selectedRows[object.uuid];
      if (isSelected) {
        // Si ya estaba seleccionado, lo quitamos del array
        return prev.filter((item) => item.uuid !== object.uuid);
      } else {
        // Si no estaba seleccionado, lo agregamos
        return [...prev, object];
      }
    });
    setSelectAllPages(false);
  };

  // Función para seleccionar/deseleccionar todos en la página actual
  const handleSelectAll = (data) => {
    const allSelected = data.every((row) => selectedRows[row.uuid]);
    setSelectedRows((prev) => {
      const newSelection = { ...prev };
      if (!allSelected) {
        data.forEach((row) => {
          newSelection[row.uuid] = true;
        });
      } else {
        data.forEach((row) => {
          delete newSelection[row.uuid];
        });
      }
      return newSelection;
    });
    setSelectedNames((prev) => {
      if (!allSelected) {
        const existingUuids = new Set(prev.map((item) => item.uuid));
        const newItems = data.filter((row) => !existingUuids.has(row.uuid));
        return [...prev, ...newItems];
      } else {
        const pageUuids = new Set(data.map((row) => row.uuid));
        return prev.filter((item) => !pageUuids.has(item.uuid));
      }
    });
    setSelectAllPages(false);
  };

  // Función para seleccionar todos los elementos de todas las páginas
  const handleSelectAllPages = async () => {
    if (selectAllPages) {
      // Deseleccionar todos
      setSelectedRows({});
      setSelectedNames([]);
      setSelectAllPages(false);
      setAllData([]);
    } else {
      try {
        // Obtener todos los datos sin paginación
        const filter = {
          page: 1,
          pageSize: 9999, // Número grande para obtener todos
        };
        if (filters) {
          filter.filters = filters;
        }
        const result = await WorkingPartServices.listFilter(filter);
        const allItems = result?.docs || [];
        setAllData(allItems);

        // Seleccionar todos
        const newSelection = {};
        allItems.forEach((item) => {
          newSelection[item.uuid] = true;
        });
        setSelectedRows(newSelection);
        setSelectedNames(allItems);
        setSelectAllPages(true);
      } catch (error) {
        console.error("Error al obtener todos los datos:", error);
        alert(
          t("forms.workingPartSnap.messages.errorLoadingAll") ||
            "Error al cargar todos los elementos",
        );
      }
    }
  };

  // Función para enviar los seleccionados al backend
  const handleSendConceptsSelected = async () => {
    const selectedUuids = Object.keys(selectedRows).filter(
      (uuid) => selectedRows[uuid],
    );

    // if (selectedUuids.length === 0) {
    //   alert(
    //     t("forms.workingPartSnap.messages.noItemsSelected") ||
    //       "No hay elementos seleccionados"
    //   );
    //   return;
    // }

    try {
      const array = {
        uuids: selectedUuids,
      };

      let title, text, icon;
      const okAction = () => {
        setSelectedRows({});
        setSelectedNames([]);
        setSelectAllPages(false);
        setAllData([]);
        setForceRefresh(true);
      };
      const okAction2 = async () => {
        try {
          let result = await WorkingPartServices.sendMultipleA3(array);

          if (result) {
            title = t("forms.workingPartSnap.messages.sendA3.title");
            text = t("forms.workingPartSnap.messages.sendA3.success");
            icon = "success";
          } else {
            title = "Error";
            text = t("forms.workingPartSnap.messages.sendA3.error");
          }

          showDialog(title, text, icon, false, "OK", false, okAction);
        } catch (error) {
          console.error("Error al enviar:", error);
        }
      };
      showDialog(
        t("forms.workingPartDetail.messages.concepts.title"),
        t("forms.workingPartDetail.messages.concepts.confirmText"),
        "warning",
        true,
        t("forms.workingPartDetail.messages.concepts.confirmButton"),
        false,
        okAction2,
        t("forms.workingPartDetail.messages.concepts.cancelButton"),
      );
    } catch (error) {
      console.error("Error al enviar:", error);
    }
  };

  const handleSendConceptsSelected2 = async (uuid = null) => {
    let isMultiple = true;

    const selectedUuids = Object.keys(selectedRows).filter(
      (uuid) => selectedRows[uuid],
    );

    if (selectedUuids.length === 1) {
      await putConcepts(selectedUuids[0], selectedUuids, (isMultiple = false));
    } else {
      await putConcepts(uuid, selectedUuids, isMultiple, selectedNames);
    }
  };

  const handleSendJourneysSelected = async () => {
    const selectedUuids = Object.keys(selectedRows).filter(
      (uuid) => selectedRows[uuid],
    );

    if (selectedUuids.length === 0) {
      return;
    }

    try {
      const array = {
        uuids: selectedUuids,
      };

      let title, text, icon;
      const okAction = () => {
        setSelectedRows({});
        setSelectedNames([]);
        setSelectAllPages(false);
        setAllData([]);
        setForceRefresh(true);
      };

      const okAction2 = async () => {
        try {
          let result = await WorkingPartServices.sendMultipleJourneysA3(array);
          if (result) {
            setForceRefresh(true);
            setSelectedRows({});

            title = t("forms.workingPartSnap.messages.sendJourneys.title");
            text = t("forms.workingPartSnap.messages.sendJourneys.success");
            icon = "success";
          } else {
            title = "Error";
            text = t("forms.workingPartSnap.messages.sendJourneys.error");
          }
          showDialog(title, text, icon, false, "OK", false);
        } catch (error) {
          console.error("Error al enviar jornadas a A3:", error);
        }
      };

      showDialog(
        t("forms.workingPartDetail.messages.journey.title"),
        t("forms.workingPartDetail.messages.journey.confirmText"),
        "warning",
        true,
        t("forms.workingPartDetail.messages.journey.confirmButton"),
        false,
        okAction2,
        t("forms.workingPartDetail.messages.journey.cancelButton"),
      );
    } catch (error) {
      console.error("Error al enviar jornadas a A3:", error);
    }
  };

  const columns = [
    {
      header: ({ table }) => {
        const data = table.getRowModel().rows.map((row) => row.original);
        const allSelected =
          data.length > 0 && data.every((row) => selectedRows[row.uuid]);
        return (
          <input
            type="checkbox"
            checked={allSelected}
            onChange={() => handleSelectAll(data)}
            style={{ cursor: "pointer" }}
          />
        );
      },
      accessorKey: "select",
      size: 50,
      cell: (info) => (
        <input
          type="checkbox"
          checked={!!selectedRows[info.row.original.uuid]}
          onChange={() => handleSelectRow(info.row.original)}
          style={{ cursor: "pointer" }}
        />
      ),
    },
    {
      header: t("forms.workingPartSnap.table.period"),
      accessorKey: "month",
      cell: (info) => {
        if (info.row.original.month) {
          return `${info.row.original.month}/${info.row.original.year}`;
        }
        return info.getValue();
      },
    },
    // {
    //   header: t("forms.workingPartSnap.table.date"),
    //   accessorKey: "dateGenerated",
    //   cell: (info) => moment(info.getValue()).format("DD-MM-yyyy HH:mm"),
    // },
    {
      header: t("forms.workingPartSnap.table.employeeName"),
      accessorKey: "employeeName",
      cell: (info) => info.getValue(),
    },
    {
      header: t("forms.workingPartSnap.table.contractType"),
      accessorKey: "employeeData.indefinedContract",
      cell: (info) => {
        const data = info.getValue();
        return data ? "Indefinido" : "Temporal";
      },
    },
    // {
    //   header: t("forms.workingPartSnap.table.totalParts"),
    //   accessorKey: "totalParts",
    //   cell: (info) => info.getValue(),
    // },
    {
      header: t("forms.workingPartSnap.table.totalHours"),
      accessorKey: "totalHours",
      cell: (info) => {
        const numberTrunk = Math.trunc(info.getValue());
        return numberTrunk;
      },
    },
    {
      header: t("forms.workingPartSnap.table.pending"),
      accessorKey: "",
      cell: (info) => {
        const concept = info.row.original;
        const conceptSended =
          concept.conceptData.status === "PENDING" ? "Si" : "No";
        const journeySended = concept.sendedA3 ? "Si" : "No";
        return `${journeySended} / ${conceptSended}`;
      },
    },
    {
      header: t("forms.workingPartSnap.table.normalHours"),
      accessorKey: "conceptData.items",
      cell: (info) => {
        const suma = info.getValue().reduce((acc, item) => {
          return acc + (item.hoursByType?.[1] || 0);
        }, 0);
        return suma.toFixed(2);
      },
    },
    {
      header: t("forms.workingPartSnap.table.nocturnHours"),
      accessorKey: "conceptData.items",
      cell: (info) => {
        const suma = info.getValue().reduce((acc, item) => {
          return acc + (item.hoursByType?.[5] || 0);
        }, 0);
        return suma.toFixed(2);
      },
    },
    // {
    //   header: t("forms.workingPartSnap.table.extraHours"),
    //   accessorKey: "conceptData.items",
    //   cell: (info) => {
    //     const suma = info.getValue().reduce((acc, item) => {
    //       return acc + (item.hoursByType?.[2] || 0);
    //     }, 0);
    //     return suma.toFixed(2);
    //   },
    // },

    {
      header: t("forms.workingPartSnap.table.fitos"),
      accessorKey: "conceptData.items",
      cell: (info) => {
        const suma = info.getValue().reduce((acc, item) => {
          return acc + (item.hoursByType?.[4] || 0);
        }, 0);
        return suma.toFixed(2);
      },
    },

    {
      header: t("forms.workingPartSnap.table.compensation"),
      accessorKey: "hoursByType",
      cell: (info) => info.getValue()[7] || 0,
    },
    {
      header: t("forms.workingPartSnap.table.totalKms"),
      accessorKey: "totalKms",
      cell: (info) => info.getValue(),
    },
    {
      header: t("forms.workingPartSnap.table.peonadas"),
      accessorKey: "",
      cell: (info) => {
        const datos = info.row.original;
        const numberTrunkPeonadas = Math.trunc(datos.peonadas);
        return `${numberTrunkPeonadas} / ${datos.totalParts}`;
      },
    },
    {
      header: "",
      accessorKey: "ac",
      size: 50,
      cell: (info) => renderAction(info.row.original),
    },
  ];

  const renderAction = (data) => {
    return (
      <>
        <div className="dropdown text-center">
          <ButtonAction
            onClick={() => viewWorkingsPart(data)}
            title={t("forms.workingPartSnap.labels.viewResume")}
          >
            <Icon className="fa-solid fa-arrow-right-long"></Icon>
          </ButtonAction>
        </div>
      </>
    );
  };

  const viewWorkingsPart = async (data) => {
    navigate(`${PATHS.WORKING_PART_SNAP_DETAIL.replace(":uuid", data.uuid)}`);
  };

  const listData = async (filter) => {
    const result = await WorkingPartServices.listFilter(filter);
    console.log(result);
    return result;
  };

  const hideColumn = {};
  let resize = null;

  const selectedCount = Object.values(selectedRows).filter(Boolean).length;

  return (
    <>
      {/* {selectedCount > 0 && ( */}

      <div
        style={{
          padding: "10px",
          marginBottom: "10px",
          // backgroundColor: "#f0f0f0",
          borderRadius: "4px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "10px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "15px" }}>
          <span style={{ fontWeight: "bold" }}>
            {selectedCount}{" "}
            {t("forms.workingPartSnap.labels.itemsSelected") ||
              "elementos seleccionados"}
          </span>
          {/* {!selectAllPages && (
              <button
                onClick={handleSelectAllPages}
                style={{
                  padding: "6px 12px",
                  backgroundColor: "#6c757d",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontSize: "14px",
                }}
              >
                {t("forms.workingPartSnap.labels.selectAll") ||
                  "Seleccionar todos"}
              </button>
            )} */}
          {/* {selectAllPages && (
              <button
                onClick={handleSelectAllPages}
                style={{
                  padding: "6px 12px",
                  backgroundColor: "#dc3545",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontSize: "14px",
                }}
              >
                {t("forms.workingPartSnap.labels.deselectAll") ||
                  "Deseleccionar todos"}
              </button>
            )} */}
        </div>
        <div className="d-flex gap-2">
          <button
            onClick={handleSendJourneysSelected}
            // disabled={selectedCount === 0}

            className="btn btn-primary"
          >
            {t("forms.workingPartSnap.labels.sendJourneysSelected")}
          </button>
          <button
            onClick={handleSendConceptsSelected2}
            // disabled={selectedCount === 0}

            className="btn btn-primary  "
            // style={{
            //   padding: "8px 16px",
            //   backgroundColor: "#f9ae17",
            //   color: "white",
            //   border: "none",
            //   borderRadius: "4px",
            //   fontWeight: "bold",
            // }}
          >
            {t("forms.workingPartSnap.labels.sendSelected") ||
              "Enviar seleccionados"}
          </button>
        </div>
      </div>
      {/* )} */}
      <PaginateList
        refresh={forceRefresh}
        filters={filters}
        columns={columns}
        toList={listData}
        hideColum={hideColumn}
        resize={resize}
        resetRefresh={() => setForceRefresh(false)}
      ></PaginateList>
    </>
  );
};

export default WorkingPartListContainer;
